import {createTheme, type Theme} from '@mui/material/styles';
import {deepmerge} from '@mui/utils';
import type {BrandTokens, OlympusFontTokens} from '@olympus-frontend/tenant-config';
import {baseTokens, DEFAULT_BORDER_WIDTHS, DEFAULT_RADII, DEFAULT_SHADOWS} from './base-tokens';

export function createOlympusTheme(brand: BrandTokens, direction: 'ltr' | 'rtl' = 'ltr'): Theme {
  const merged = deepmerge(baseTokens, brand);
  const colors = merged.palette.colors;
  const arabic = merged.fontFamilyArabic ?? merged.font.family.secondary;
  const font: OlympusFontTokens = {
    ...merged.font,
    family: direction === 'rtl' ? {primary: arabic, secondary: arabic} : merged.font.family,
  };
  const {size, weight} = font;

  const muiTypography = {
    fontFamily: font.family.secondary,
    // Both language stacks, exposed direction-independently so components can pin
    // a run of text to a specific language's font regardless of the page locale
    // (e.g. the locale switcher labels each option in its own language's font).
    fontFamilyLatin: merged.font.family.secondary,
    fontFamilyArabic: arabic,
    fontWeightLight: weight.light,
    fontWeightRegular: weight.regular,
    fontWeightMedium: weight.medium,
    fontWeightBold: weight.bold,
    h1: {fontFamily: font.family.primary, fontSize: size['title-1'], fontWeight: weight.bold, lineHeight: 1.2},
    h2: {fontFamily: font.family.primary, fontSize: size['title-2'], fontWeight: weight.bold, lineHeight: 1.2},
    h3: {fontFamily: font.family.primary, fontSize: size['title-4'], fontWeight: weight.bold, lineHeight: 1.2},
    body1: {fontSize: size['body-2'], fontWeight: weight.regular, lineHeight: 1.2},
    body2: {fontSize: size['body-3'], fontWeight: weight.regular, lineHeight: 1.2},
    caption: {fontSize: size.caption, fontWeight: weight.regular},
  } as never;

  return createTheme({
    direction,
    palette: {
      primary: {
        main: colors.surface.primary.default,
        light: colors.icon.primary.inverse,
        dark: colors.surface.primary.active,
        contrastText: colors.text.primary.onColor,
      },
      error: {main: colors.surface.error.default},
      warning: {main: colors.surface.warning.default},
      success: {main: colors.surface.success.default},
      background: {default: colors.surface.neutral.page, paper: colors.surface.neutral.default},
      text: {
        primary: colors.text.neutral.default,
        secondary: colors.text.neutral.muted,
        disabled: colors.text.neutral.disabled,
      },
      colors,
    },
    typography: muiTypography,
    font,
    shape: {borderRadius: merged.shape?.borderRadius ?? 8},
    radii: merged.radii ?? DEFAULT_RADII,
    borderWidths: merged.borderWidths ?? DEFAULT_BORDER_WIDTHS,
    customShadows: merged.shadows ?? DEFAULT_SHADOWS,
    ...(merged.breakpoints && {
      breakpoints: {
        values: {
          xs: merged.breakpoints.xs,
          sm: merged.breakpoints.sm,
          md: merged.breakpoints.md,
          lg: merged.breakpoints.lg,
          xl: merged.breakpoints.xl,
        },
      },
    }),
    ...(merged.components && {components: merged.components}),
  });
}
